/**
 * Import CRS: GeoJSON/DXF coordinates may be WGS84 (EPSG:4326) or S-JTSK meters (EPSG:5514).
 * Auto-detect prefers EPSG:5514 when coordinate magnitude matches Czech planar meters.
 */

export const IMPORT_SOURCE_CRS_CODES = ['EPSG:5514', 'EPSG:4326'] as const;
export type ImportSourceCrsCode = (typeof IMPORT_SOURCE_CRS_CODES)[number];
export type ImportSourceCrsInput = ImportSourceCrsCode | 'auto' | string | null | undefined;

/** Resolved CRS used for transform (never `auto`). */
export type ResolvedImportSourceCrs = ImportSourceCrsCode;

export interface ImportSourceCrsSample {
  type?: string;
  coordinates?: unknown;
}

export interface ImportSourceCrsCollection {
  type?: string;
  features?: Array<{ geometry?: ImportSourceCrsSample | null }>;
}

/**
 * Czech S-JTSK (EPSG:5514 / Křovák) typical ranges in PostGIS axis order (X easting-ish, Y northing-ish):
 * both axes are large negative meters for CZ territory.
 */
const SJTSK_ABS_MIN_M = 200;
const SJTSK_ABS_TYPICAL_MIN_M = 50_000;
const SJTSK_ABS_MAX_M = 2_500_000;

function flattenXy(coords: unknown, out: Array<[number, number]>, limit: number): void {
  if (out.length >= limit || !Array.isArray(coords)) return;
  if (coords.length >= 2 && typeof coords[0] === 'number' && typeof coords[1] === 'number') {
    out.push([Number(coords[0]), Number(coords[1])]);
    return;
  }
  for (const child of coords) {
    flattenXy(child, out, limit);
    if (out.length >= limit) return;
  }
}

/** Collect up to `limit` sample XY pairs from a FeatureCollection / geometry. */
export function sampleImportCoordinates(
  data: ImportSourceCrsCollection | ImportSourceCrsSample | null | undefined,
  limit = 64,
): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  if (!data || typeof data !== 'object') return out;
  if (Array.isArray((data as ImportSourceCrsCollection).features)) {
    for (const f of (data as ImportSourceCrsCollection).features!) {
      if (out.length >= limit) break;
      flattenXy(f?.geometry?.coordinates, out, limit);
    }
    return out;
  }
  flattenXy((data as ImportSourceCrsSample).coordinates, out, limit);
  return out;
}

function looksLikeWgs84Pair(x: number, y: number): boolean {
  return Number.isFinite(x) && Number.isFinite(y) && Math.abs(x) <= 180 && Math.abs(y) <= 90;
}

/**
 * Heuristic: S-JTSK meters if |x| and |y| are both large (beyond degree range),
 * especially in the typical CZ meter band. Prefer 5514 when ambiguous planar meters.
 */
export function looksLikeSjtskPair(x: number, y: number): boolean {
  if (!Number.isFinite(x) || !Number.isFinite(y)) return false;
  const ax = Math.abs(x);
  const ay = Math.abs(y);
  if (ax <= 180 && ay <= 90) return false;
  if (ax < SJTSK_ABS_MIN_M || ay < SJTSK_ABS_MIN_M) return false;
  if (ax > SJTSK_ABS_MAX_M || ay > SJTSK_ABS_MAX_M) return false;
  // Strong CZ signal: both axes in tens/hundreds of km
  if (ax >= SJTSK_ABS_TYPICAL_MIN_M && ay >= SJTSK_ABS_TYPICAL_MIN_M) return true;
  // Planar meters outside WGS84 degree box → prefer 5514 for this product
  return true;
}

/**
 * Detect input CRS from coordinate magnitude.
 * Prefers EPSG:5514 when any sample looks like S-JTSK meters; else EPSG:4326 if all look like degrees;
 * default EPSG:5514 when empty (CAD/DXF default for TelcoTrack).
 */
export function detectImportSourceCrs(
  data: ImportSourceCrsCollection | ImportSourceCrsSample | null | undefined,
): ResolvedImportSourceCrs {
  const samples = sampleImportCoordinates(data);
  if (samples.length === 0) return 'EPSG:5514';

  let sjtskVotes = 0;
  let wgsVotes = 0;
  for (const [x, y] of samples) {
    if (looksLikeSjtskPair(x, y)) sjtskVotes += 1;
    else if (looksLikeWgs84Pair(x, y)) wgsVotes += 1;
  }

  if (sjtskVotes > 0) return 'EPSG:5514';
  if (wgsVotes > 0 && sjtskVotes === 0) return 'EPSG:4326';
  return 'EPSG:5514';
}

/** Normalize API/UI input (`auto`, `EPSG:5514`, `5514`, …). */
export function normalizeImportSourceCrsInput(value: ImportSourceCrsInput): ImportSourceCrsInput {
  if (value == null || value === '') return 'auto';
  const raw = String(value).trim();
  if (!raw || raw.toLowerCase() === 'auto') return 'auto';
  const upper = raw.toUpperCase().replace(/\s+/g, '');
  if (upper === 'EPSG:5514' || upper === '5514' || upper === 'S-JTSK' || upper === 'SJTSK') {
    return 'EPSG:5514';
  }
  if (upper === 'EPSG:4326' || upper === '4326' || upper === 'WGS84') {
    return 'EPSG:4326';
  }
  return raw;
}

/**
 * Resolve explicit override or auto-detect.
 * Invalid explicit values fall back to auto-detect.
 */
export function resolveImportSourceCrs(
  data: ImportSourceCrsCollection | ImportSourceCrsSample | null | undefined,
  requested?: ImportSourceCrsInput,
): ResolvedImportSourceCrs {
  const normalized = normalizeImportSourceCrsInput(requested);
  if (normalized === 'EPSG:5514' || normalized === 'EPSG:4326') return normalized;
  return detectImportSourceCrs(data);
}

/** Whether import should treat coordinates as WGS84 degrees (transform → 5514). */
export function importSourceCrsIsWgs84(resolved: ResolvedImportSourceCrs): boolean {
  return resolved === 'EPSG:4326';
}
